/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Backdrop,
  CircularProgress,
  useTheme,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material"
import { collection, addDoc, doc, updateDoc, getDoc, query, where, getDocs, DocumentSnapshot } from "firebase/firestore"
import { db } from "../../firebase"
import { toast } from 'react-toastify'
import dayjs from "dayjs"
import { tokens } from "../../theme"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import { IBusinessHoliday, isRangeFullyHoliday } from "../../utils/businessHolidays"

const MONTHS = [
  { value: "04", label: "４月" },
  { value: "05", label: "５月" },
  { value: "06", label: "６月" },
  { value: "07", label: "７月" },
  { value: "08", label: "８月" },
  { value: "09", label: "９月" },
  { value: "10", label: "１０月" },
  { value: "11", label: "１１月" },
  { value: "12", label: "１２月" },
  { value: "01", label: "１月" },
  { value: "02", label: "２月" },
  { value: "03", label: "３月" },
]

interface HygieneData {
  id?: string
  year: string
  month: string
  manager: string
  facilityChecks: Record<string, boolean>
  calibrationChecks: Record<string, boolean>
  inspectionChecks: Record<string, boolean>
  remarks: string
  verifier: string
  createdAt?: string
  updatedAt?: string
}

const HygieneManagementForm = () => {
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const isEditMode = !!(id && id !== 'new')

  const [processing, setProcessing] = useState<boolean>(false)
  const [year, setYear] = useState<string>(searchParams.get('year') || dayjs().format("YYYY"))
  const [month, setMonth] = useState<string>(searchParams.get('month') || dayjs().format("MM"))
  const periodFixed = !!(searchParams.get('year') && searchParams.get('month'))
  const [manager, setManager] = useState<string>("")
  const [facilityChecks, setFacilityChecks] = useState<Record<string, boolean>>({})
  const [calibrationChecks, setCalibrationChecks] = useState<Record<string, boolean>>({
    thermometer: false,
    scale: false,
    metalDetector: false,
    inspectionEquipment: false,
  })
  const [inspectionChecks, setInspectionChecks] = useState<Record<string, boolean>>({})
  const [remarks, setRemarks] = useState<string>("")
  const [verifier, setVerifier] = useState<string>("")

  const facilityItems = [
    "昆虫は発生していないか",
    "水たまりはないか",
    "不要な物はないか、整理整頓されているか",
    "草などはのびたままになっていないか",
    "排水溝の管理は適切か",
  ]

  const inspectionItems = [
    "製品（１ヶ月ごと）",
    "設備及び器具（１ヵ月ごと）",
    "枝肉ふき取り",
  ]

  useEffect(() => {
    // Initialize facility checks
    const initialFacilityChecks: Record<string, boolean> = {}
    facilityItems.forEach(item => {
      initialFacilityChecks[item] = false // Default to unchecked
    })
    setFacilityChecks(initialFacilityChecks)

    // Initialize inspection checks
    const initialInspectionChecks: Record<string, boolean> = {}
    inspectionItems.forEach(item => {
      initialInspectionChecks[item] = false
    })
    setInspectionChecks(initialInspectionChecks)
  }, [])

  useEffect(() => {
    if (isEditMode) {
      loadRecord(id)
    } else {
      // Check if record already exists for current year/month
      checkExistingRecord(year, month)
    }
  }, [id, isEditMode])

  const checkExistingRecord = async (targetYear: string, targetMonth: string) => {
    setProcessing(true)
    try {
      const q = query(
        collection(db, "monthlyHygieneManagements"),
        where("year", "==", targetYear),
        where("month", "==", targetMonth)
      )
      const querySnapshot = await getDocs(q)

      if (!querySnapshot.empty) {
        // Record exists, load it as edit mode
        const existingDoc = querySnapshot.docs[0]
        const record = {
          id: existingDoc.id,
          ...existingDoc.data()
        } as HygieneData

        toast.info("既に入力済みのデータがあります。編集モードで開きます。")

        setYear(record.year)
        setMonth(record.month)
        setManager(record.manager)
        setFacilityChecks(record.facilityChecks || {})
        setCalibrationChecks(record.calibrationChecks || {
          thermometer: false,
          scale: false,
          metalDetector: false,
          inspectionEquipment: false,
        })
        setInspectionChecks(record.inspectionChecks || {})
        setRemarks(record.remarks || "")
        setVerifier(record.verifier || "")

        // Update URL to edit mode without page reload
        navigate(`/admin/hygiene-management/${existingDoc.id}`, { replace: true })
        return
      }

      // 休業日マスタでこの月の全日が休業に該当するか確認
      const firstDay = `${targetYear}-${targetMonth}-01`
      const lastDay = dayjs(firstDay).endOf('month').format("YYYY-MM-DD")
      const holidaysSnapshot = await getDocs(collection(db, "businessHolidays"))
      const holidays = holidaysSnapshot.docs.map(d => ({ id: d.id, ...d.data() } as IBusinessHoliday))

      if (isRangeFullyHoliday(firstDay, lastDay, holidays)) {
        toast.info("この月は休業期間として登録されています。一覧画面に戻ります。")
        navigate('/admin/hygiene-management', { replace: true })
        return
      }
    } catch (e) {
      console.error("Error checking existing record:", e)
      toast.error("データの確認に失敗しました")
    } finally {
      setProcessing(false)
    }
  }

  const loadRecord = async (recordId: string) => {
    setProcessing(true)
    try {
      const docRef = doc(db, "monthlyHygieneManagements", recordId)
      const docSnap: DocumentSnapshot = await getDoc(docRef)

      if (docSnap.exists()) {
        const record = {
          id: docSnap.id,
          ...docSnap.data()
        } as HygieneData

        setYear(record.year)
        setMonth(record.month)
        setManager(record.manager)
        setFacilityChecks(record.facilityChecks || {})
        setCalibrationChecks(record.calibrationChecks || {
          thermometer: false,
          scale: false,
          metalDetector: false,
          inspectionEquipment: false,
        })
        setInspectionChecks(record.inspectionChecks || {})
        setRemarks(record.remarks || "")
        setVerifier(record.verifier || "")
      } else {
        toast.error("レコードが見つかりません")
        navigate('/admin/hygiene-management')
      }
    } catch (e) {
      toast.error("データの読み込みに失敗しました: " + JSON.stringify(e))
    } finally {
      setProcessing(false)
    }
  }

  const handleFacilityCheck = (item: string, checked: boolean) => {
    setFacilityChecks(prev => ({
      ...prev,
      [item]: checked
    }))
  }

  const handleInspectionCheck = (item: string, checked: boolean) => {
    setInspectionChecks(prev => ({
      ...prev,
      [item]: checked
    }))
  }

  const handleCalibrationCheck = (item: string, checked: boolean) => {
    setCalibrationChecks(prev => ({
      ...prev,
      [item]: checked
    }))
  }

  const saveData = async () => {
    if (!manager) {
      toast.error("衛生管理者担当責任者を入力してください")
      return
    }

    setProcessing(true)
    const data = {
      year,
      month,
      manager,
      facilityChecks,
      calibrationChecks,
      inspectionChecks,
      remarks,
      verifier,
      updatedAt: dayjs().format("YYYY-MM-DD HH:mm:ss")
    }

    try {
      if (isEditMode) {
        // Update existing record
        await updateDoc(doc(db, "monthlyHygieneManagements", id), data)
        toast.success("衛生管理点検票を更新しました")
      } else {
        // Check if record already exists for this period
        const q = query(
          collection(db, "monthlyHygieneManagements"),
          where("year", "==", year),
          where("month", "==", month)
        )
        const querySnapshot = await getDocs(q)

        if (!querySnapshot.empty) {
          toast.error("この月のデータは既に存在します")
          setProcessing(false)
          return
        }

        // Create new record
        const newData = {
          ...data,
          createdAt: dayjs().format("YYYY-MM-DD HH:mm:ss")
        }
        await addDoc(collection(db, "monthlyHygieneManagements"), newData)
        toast.success("衛生管理点検票を保存しました")
      }
      navigate('/admin/hygiene-management')
    } catch (e) {
      toast.error("保存に失敗しました: " + JSON.stringify(e))
    } finally {
      setProcessing(false)
    }
  }

  const handleBack = () => {
    navigate('/admin/hygiene-management')
  }

  return (
    <Box m={3}>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
        >
          戻る
        </Button>
        <Typography variant="h4" fontWeight="bold">
          {isEditMode ? '衛生管理点検票(月次) 編集' : '衛生管理点検票(月次) 新規作成'}
        </Typography>
      </Box>

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2} mb={3}>
          <TextField
            label="年度"
            type="number"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            fullWidth
            disabled={isEditMode || periodFixed}
          />
          <FormControl fullWidth disabled={isEditMode || periodFixed}>
            <InputLabel>対象月</InputLabel>
            <Select
              value={month}
              label="対象月"
              onChange={(e) => setMonth(e.target.value)}
            >
              {MONTHS.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="衛生管理者担当責任者"
            value={manager}
            onChange={(e) => setManager(e.target.value)}
            fullWidth
            required
          />
        </Box>

        {/* 施設周囲に関して */}
        <Typography variant="h6" fontWeight="bold" mt={3} mb={2}>
          施設周囲に関して
        </Typography>
        <Typography variant="body2" color="textSecondary" mb={2}>
          適の場合は✓、不適の場合はチェックを外す（×の場合は、措置結果を特記事項に記載すること）
        </Typography>
        <Paper sx={{ p: 2, mb: 3, backgroundColor: colors.primary[400] }}>
          {facilityItems.map((item, index) => (
            <FormControlLabel
              key={index}
              control={
                <Checkbox
                  checked={facilityChecks[item] || false}
                  onChange={(e) => handleFacilityCheck(item, e.target.checked)}
                  color="primary"
                />
              }
              label={item}
              sx={{ display: 'block', mb: 1 }}
            />
          ))}
        </Paper>

        {/* 機器の校正に関して */}
        <Typography variant="h6" fontWeight="bold" mt={3} mb={2}>
          機器の校正に関して
        </Typography>
        <Typography variant="body2" color="textSecondary" mb={2}>
          校正、自主検査を実施したとき、チェックを入れること
        </Typography>
        <Paper sx={{ p: 2, mb: 3, backgroundColor: colors.primary[400] }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={calibrationChecks.thermometer}
                onChange={(e) => handleCalibrationCheck("thermometer", e.target.checked)}
              />
            }
            label="温度計"
            sx={{ display: 'block', mb: 1 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={calibrationChecks.scale}
                onChange={(e) => handleCalibrationCheck("scale", e.target.checked)}
              />
            }
            label="計量器"
            sx={{ display: 'block', mb: 1 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={calibrationChecks.metalDetector}
                onChange={(e) => handleCalibrationCheck("metalDetector", e.target.checked)}
              />
            }
            label="金属探知機"
            sx={{ display: 'block', mb: 1 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={calibrationChecks.inspectionEquipment}
                onChange={(e) => handleCalibrationCheck("inspectionEquipment", e.target.checked)}
              />
            }
            label="検査機器（ふ卵器・はかり・滅菌機）"
            sx={{ display: 'block', mb: 1 }}
          />
        </Paper>

        {/* 自主検査に関して */}
        <Typography variant="h6" fontWeight="bold" mt={3} mb={2}>
          自主検査に関して
        </Typography>
        <Typography variant="body2" color="textSecondary" mb={2}>
          自主検査実施時にチェックを入れること（自主検査結果は別紙の自主検査結果表に記録する）
        </Typography>
        <Paper sx={{ p: 2, mb: 3, backgroundColor: colors.primary[400] }}>
          {inspectionItems.map((item, index) => (
            <FormControlLabel
              key={index}
              control={
                <Checkbox
                  checked={inspectionChecks[item] || false}
                  onChange={(e) => handleInspectionCheck(item, e.target.checked)}
                  color="primary"
                />
              }
              label={item}
              sx={{ display: 'block', mb: 1 }}
            />
          ))}
        </Paper>

        {/* 確認者 */}
        <Typography variant="h6" fontWeight="bold" mt={3} mb={2}>
          確認者
        </Typography>
        <TextField
          value={verifier}
          onChange={(e) => setVerifier(e.target.value)}
          fullWidth
          placeholder="確認者名を入力"
          sx={{ mb: 3 }}
        />

        {/* 特記事項 */}
        <Typography variant="h6" fontWeight="bold" mt={3} mb={2}>
          特記事項
        </Typography>
        <TextField
          multiline
          rows={4}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          fullWidth
          placeholder="×の場合は、措置結果をここに記載すること"
        />

        <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
          <Button
            variant="outlined"
            color="secondary"
            size="large"
            onClick={handleBack}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={saveData}
            disabled={processing}
          >
            {isEditMode ? "更新" : "保存"}
          </Button>
        </Box>
      </Paper>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={processing}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  )
}

export default HygieneManagementForm
